import { Formik } from "formik";

import { BTInput, BTLabel, BTText } from "@btnet/core-components";

import { BdsButton, BdsFormItem } from "@buildertrend/components";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import yup from "utilities/form/yup";

import { BTModal, type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";

import "./EditUsernameModal.less";

interface IEditUsernameModalProps {
    bodyCopy: string;
    currentUsername: string;
    loading: boolean;
    modalConfig?: IModalConfiguration;
    newEmailHelperText: string | undefined;
    newEmailSubmitText: string | JSX.Element | undefined;
    visible: boolean;

    onBeforeClose?: () => void;
    onEditUsernameSuccess?: () => void;
    onUsernameEdited: (newUsername: string) => Promise<void>;
    setLoading: (loading: boolean) => void;
}

interface IFormValues {
    newUsername: string;
}

const validationSchema = yup.object().shape({
    newUsername: yup
        .string()
        .email("Must be valid email address")
        .required()
        .label("New email address"),
});

export const EditUsernameModal = (props: IEditUsernameModalProps) => {
    const {
        bodyCopy,
        currentUsername,
        loading,
        modalConfig,
        newEmailHelperText,
        newEmailSubmitText,
        visible,
        onBeforeClose,
        onEditUsernameSuccess,
        onUsernameEdited,
        setLoading,
    } = props;

    return (
        <Formik<IFormValues>
            initialValues={{ newUsername: "" }}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={async (values) => {
                setLoading(true);
                try {
                    await onUsernameEdited(values.newUsername);
                    setLoading(false);
                    onEditUsernameSuccess && onEditUsernameSuccess();
                } catch (err: unknown) {
                    setLoading(false);
                    showApixErrorMessage(err as Error);
                }
            }}
        >
            {({ values, touched, errors, setFieldValue, setFieldTouched, submitForm }) => {
                const fieldError =
                    touched.newUsername || values.newUsername !== ""
                        ? errors.newUsername
                        : undefined;
                return (
                    <BTModal
                        width="560px"
                        title="Edit email address"
                        data-testid="editEmailConfirm"
                        visible={visible}
                        closable={true}
                        beforeClose={() => onBeforeClose && onBeforeClose()}
                        className="EditUsername-Modal"
                        {...modalConfig}
                    >
                        <p>{bodyCopy}</p>
                        <div data-testid="changeUsernameForm" className="EditUsername-Form">
                            <div className="bt-flex-row EditUsername-Row EditUsername-Current">
                                <BTLabel
                                    text="Current email address"
                                    className="EditUsername-Current-Label"
                                />
                                <BTText
                                    text={currentUsername}
                                    className="EditUsername-Current-Text"
                                    size="normal-md"
                                />
                            </div>
                            <div className="bt-flex-row EditUsername-Row EditUsername-New">
                                <BdsFormItem
                                    label="New email address"
                                    validateStatus={
                                        fieldError ? "error" : touched.newUsername ? "success" : ""
                                    }
                                    help={fieldError ?? newEmailHelperText}
                                    className="EditUsername-New-Input"
                                >
                                    <BTInput
                                        id="newUsername"
                                        data-testid="newUsername-input"
                                        value={values.newUsername}
                                        onChange={(_id, value) =>
                                            setFieldValue("newUsername", value)
                                        }
                                        onBlur={() => setFieldTouched("newUsername", true)}
                                    />
                                </BdsFormItem>
                            </div>
                            <div className="EditUsername-Footer">
                                <BdsButton
                                    testid="btnNewEmailSubmit"
                                    text={newEmailSubmitText}
                                    ariaLabel="Submit"
                                    loading={loading}
                                    className="EditUsername-Footer-Button"
                                    onClick={() => submitForm()}
                                />
                            </div>
                        </div>
                    </BTModal>
                );
            }}
        </Formik>
    );
};
